import { useMatch, useNavigate } from "react-router-dom";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AttributionTab } from "./AttributionTab";
import { DashboardLibraryTab } from "./DashboardLibraryTab";
import { ReportBuilder } from "./ReportBuilder";
import { ScorecardTab } from "./ScorecardTab";
import { WebAdsTab } from "./WebAdsTab";

/**
 * Reports - build and save reports (assembled into dashboards on home),
 * prove revenue with Attribution, hand clients the monthly Scorecard, and
 * start from ready-made dashboards in the library.
 */

const TABS = ["library", "scorecard", "attribution", "web", "builder"] as const;

export const ReportsPage = () => {
  const navigate = useNavigate();
  const { moduleFlags } = useConfigurationContext();
  const tabMatch = useMatch("/reports/:tab");
  const param = tabMatch?.params?.tab ?? "library";
  const currentTab = (TABS as readonly string[]).includes(param)
    ? param
    : "library";

  const handleTabChange = (value: string) => {
    navigate(value === "library" ? "/reports" : `/reports/${value}`);
  };

  if (moduleFlags?.reports === false) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-sm text-muted-foreground">
          Reports isn't turned on for this workspace yet.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Build reports here, then compose them into dashboards on your home
          screen.
        </p>
      </div>
      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="library">Report home</TabsTrigger>
          <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="attribution">Attribution</TabsTrigger>
          <TabsTrigger value="web">Web & Ads</TabsTrigger>
          <TabsTrigger value="builder">Report builder</TabsTrigger>
        </TabsList>
        <TabsContent value="builder" className="pt-4">
          <ReportBuilder />
        </TabsContent>
        <TabsContent value="attribution" className="pt-4">
          <AttributionTab />
        </TabsContent>
        <TabsContent value="web" className="pt-4">
          <WebAdsTab />
        </TabsContent>
        <TabsContent value="scorecard" className="pt-4">
          <ScorecardTab />
        </TabsContent>
        <TabsContent value="library" className="pt-4">
          <DashboardLibraryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

ReportsPage.path = "/reports";
